import { useEffect, useMemo, useState } from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import SelectSearchInput from "../SelectSearch";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import EmployeeSalesBarChart from "./EmployeeSalesBarChart";
import EmployeeTable from "./EmployeeTable";
import ItemSoldTable from "./ItemSoldTable";
import Select from 'react-select'

const EmployeeAnalysis = () => {
    const today = new Date().toISOString().split('T')[0];

    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [token, setToken] = useState("");
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [loading, setLoading] = useState(false);
    const [employeeData, setEmployeeData] = useState([]);
    const [menuData, setMenuData] = useState([]);
    const [searchByItem, setSearchByItem] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemAnalytics, setItemAnalytics] = useState(null);

    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_BASE_URL;

    const allMenuItems = useMemo(() => {
        return Array.isArray(menuData) ? menuData : [];
    }, [menuData]);

    const searchOptions = useMemo(() => {
        return allMenuItems.map(item => ({
            value: item.Name,
            label: item.Name,
            data: item
        }));
    }, [allMenuItems]);


    const fetchMenu = async () => {
        if (!token || !selectedOutlet) return;

        setLoading(true);
        await axios
            .post(`${baseUrl}/item-lists-from-orders`, {
                token: token,
                outlet: selectedOutlet,
            })
            .then((response) => {
                setMenuData(response.data);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                console.log(err);
            });
    }

    const fetchItemAnalytics = async () => {
        if (!selectedItem || !selectedOutlet || !token) {
            toast.error("Please select an item");
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${baseUrl}/item-analytics`, {
                token,
                itemName: selectedItem.value,
                startDate,
                endDate,
                outlet: selectedOutlet,
            });

            setItemAnalytics(response.data);
            setSelectedItem(null);
        } catch (error) {
            toast.error("Failed to fetch item analytics");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const storedToken = sessionStorage.getItem("token");

        if (!storedToken) {
            navigate("/");
        } else {
            setToken(storedToken);
        }
    }, [navigate]);

    const fetchEmployeeDetails = async () => {
        if (!selectedOutlet || !token) {
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${baseUrl}/employee-analytics`, {
                token: token,
                startDate: startDate,
                endDate: endDate,
                outlet: selectedOutlet,
            });

            const data = response.data || [];
            data.sort((a, b) => b.Total_Sales - a.Total_Sales);
            setEmployeeData(data);
        } catch (error) {
            toast.error("Failed to fetch employee data");
            console.error("Error fetching employee data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedOutlet && token) {
            fetchEmployeeDetails();
            fetchMenu();
        }
    }, [selectedOutlet, token]);

    const handleFetchData = async () => {
        if (!selectedOutlet) {
            toast.error("Please select an outlet");
            return;
        }
        if (!startDate || !endDate) {
            toast.error("Please select both dates");
            return;
        }

        await fetchEmployeeDetails();
    };

    const handleIndividualDetails = (employee) => {
        navigate('/employeedetails', {
            state: {
                startdate: startDate,
                enddate: endDate,
                employee_name: employee.Employee,
                outlet_name: selectedOutlet
            }
        });
    }

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow">
                <div className="max-w-6xl p-2 mt-5 mx-auto">
                    <div className="flex justify-between px-12 mb-4">
                        <div className="flex flex-col">
                            <label htmlFor="startDate" className="text-md font-semibold">Start Date: </label>
                            <input
                                type="date"
                                id="startDate"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                style={{ margin: "0" }}
                            />
                        </div>

                        <div className="flex justify-center text-center">
                            <SelectSearchInput
                                token={token}
                                setToken={setToken}
                                selectedOutlet={selectedOutlet}
                                setSelectedOutlet={setSelectedOutlet}
                            />
                        </div>

                        <div className="flex flex-col">
                            <label htmlFor="endDate" className="text-md font-semibold">End Date: </label>
                            <input
                                type="date"
                                id="endDate"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                style={{ margin: "0" }}
                            />
                        </div>
                    </div>

                    {!searchByItem && (
                        <div className="flex items-center mb-5">
                            <div className="flex-1 flex justify-start">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={searchByItem}
                                        onChange={(e) => {
                                            setSearchByItem(e.target.checked);
                                            setItemAnalytics(null);
                                            setSelectedItem(null);
                                        }}
                                    />
                                    <span className="font-medium">Search by Item</span>
                                </label>
                            </div>

                            <div className="flex-1 flex justify-center">
                                <button
                                    className="border-2 border-slate-300 hover:bg-slate-100 bg-slate-700 text-zinc-100 hover:text-zinc-700 rounded-lg px-4 py-2"
                                    onClick={handleFetchData}
                                    disabled={loading}
                                >
                                    {loading ? "Loading..." : "View"}
                                </button>
                            </div>

                            <div className="flex-1 flex justify-end" />
                        </div>
                    )}

                    {searchByItem && (
                        <div className="flex items-center mb-5">
                            <div className="flex-1 flex justify-start">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={searchByItem}
                                        onChange={(e) => {
                                            setSearchByItem(e.target.checked);
                                            setItemAnalytics(null);
                                            setSelectedItem(null);
                                        }}
                                    />
                                    <span className="font-medium">Search by Item</span>
                                </label>
                            </div>

                            <div className="flex justify-center items-center w-full max-w-sm">
                                <Select
                                    options={searchOptions}
                                    value={selectedItem}
                                    onChange={setSelectedItem}
                                    isClearable
                                    placeholder="Search menu items..."
                                    className="w-full"
                                />
                            </div>

                            <div className="flex-1 flex justify-end" >
                                <button
                                    className="border-2 border-slate-300 hover:bg-slate-100 bg-slate-700 text-zinc-100 hover:text-zinc-700 rounded-lg px-4 py-2"
                                    onClick={fetchItemAnalytics}
                                    disabled={loading}
                                >
                                    {loading ? "Loading..." : "Search"}
                                </button>
                            </div>
                        </div>
                    )}

                    {searchByItem ? (
                        <div className="flex justify-center mb-4">
                            {searchByItem && (
                                <>
                                    {!itemAnalytics ? (
                                        <div className="mt-6 p-4 rounded-lg border bg-yellow-50 text-yellow-700 w-lg mx-auto">
                                            No data available
                                        </div>
                                    ) : itemAnalytics.message ? (
                                        <div className="mt-6 p-4 rounded-lg border bg-yellow-50 text-yellow-700 w-lg mx-auto">
                                            No data available for the selected item
                                        </div>
                                    ) : (
                                        <div className="mt-6 space-y-6">

                                            {/* ITEM SUMMARY */}
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div className="p-4 rounded-lg border bg-slate-50">
                                                    <p className="text-sm text-gray-500">Item</p>
                                                    <p className="text-lg font-semibold">
                                                        {itemAnalytics.ItemName}
                                                    </p>
                                                    <p className="text-sm">
                                                        {itemAnalytics.Group} • {itemAnalytics.ItemType}
                                                    </p>
                                                </div>

                                                <div className="p-4 rounded-lg border bg-slate-50">
                                                    <p className="text-sm text-gray-500">Item Rate</p>
                                                    <p className="text-lg font-semibold">
                                                        {itemAnalytics.ItemRate}
                                                    </p>
                                                </div>

                                                <div className="p-4 rounded-lg border bg-slate-50">
                                                    <p className="text-sm text-gray-500">Total Sales</p>
                                                    <p className="text-lg font-semibold">
                                                        {itemAnalytics.TotalSalesAmount}
                                                    </p>
                                                    <p className="text-sm">
                                                        Quantity Sold: {itemAnalytics.TotalQuantitySold}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* EMPLOYEE WISE SALES */}
                                            <div>
                                                <h3 className="text-xl font-semibold mb-3">
                                                    Employee-wise Sales
                                                </h3>

                                                {itemAnalytics.EmployeeWiseSales?.length === 0 ? (
                                                    <div className="p-4 border rounded bg-gray-50 text-gray-600">
                                                        No employee sales data available
                                                    </div>
                                                ) : (
                                                    <div className="overflow-x-auto">
                                                        <table className="min-w-full border border-gray-200">
                                                            <thead className="bg-slate-700 text-white">
                                                                <tr>
                                                                    <th className="px-4 py-2 text-left">
                                                                        Employee
                                                                    </th>
                                                                    <th className="px-4 py-2 text-right">
                                                                        Quantity Sold
                                                                    </th>
                                                                    <th className="px-4 py-2 text-right">
                                                                        Total Sales
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {itemAnalytics.EmployeeWiseSales
                                                                    .slice()
                                                                    .sort(
                                                                        (a, b) =>
                                                                            b.QuantitySold - a.QuantitySold
                                                                    )
                                                                    .map((emp, index) => (
                                                                        <tr
                                                                            key={index}
                                                                            className="border-t"
                                                                        >
                                                                            <td className="px-4 py-2">
                                                                                {emp.Employee}
                                                                            </td>
                                                                            <td className="px-4 py-2 text-right">
                                                                                {emp.QuantitySold}
                                                                            </td>
                                                                            <td className="px-4 py-2 text-right">
                                                                                {emp.TotalSales.toFixed(2)}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            <EmployeeTable employeeData={employeeData} loading={loading} handleIndividualDetails={handleIndividualDetails} />

                            <EmployeeSalesBarChart employeeData={employeeData} />

                            <h3 className="text-xl mt-5">Items Sold</h3>

                            <ItemSoldTable data={employeeData} />
                        </>
                    )}
                </div>
            </div>

            <Footer />
        </div >
    );
};

export default EmployeeAnalysis;